import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trainingApi } from '../../api/training';
import { ApiError } from '../../api/client';
import { IconActionButton } from '../../components/IconActionButton';
import { Eye, Check, X, CheckCircle2 } from 'lucide-react';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { ReturnAction } from '../../components/ReturnAction';
import { useToast } from '../../components/Toast';

/** Only shows requests already approved by the employee's real supervisor - the final, role-based HR step. */
export function TrainingReviewPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [certificateNotes, setCertificateNotes] = useState<Record<number, string>>({});
  const pendingQuery = useQuery({ queryKey: ['training-pending-hr'], queryFn: () => trainingApi.pendingForHR() });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['training-pending-hr'] });
  const decideMutation = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: 'approved' | 'rejected' }) => trainingApi.hrDecide(id, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const completeMutation = useMutation({
    mutationFn: (id: number) => trainingApi.complete(id, { certificateNotes: certificateNotes[id] }),
    onSuccess: () => { showSuccess('Marked completed.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <PageHeader title="Training - HR Review" />
      <p className="muted">Requests already approved by the employee's supervisor, awaiting final HR approval or completion.</p>
      {pendingQuery.data && (
        <div className="panel">
          <SectionHeader title="Training Requests - HR Review" />
          <div className="panel-body">
        <DataTable
          rows={pendingQuery.data.data}
          rowKey={(r) => r.id}
          emptyMessage="Nothing pending."
          columns={[
            { header: 'Employee', render: (r) => r.employee_name },
            { header: 'Title', render: (r) => r.catalogue_title ?? r.training_title ?? '-' },
            { header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
            {
              header: '',
              render: (r) => (
                <span className="row-actions">
                  <IconActionButton icon={Eye} label="View" onClick={() => navigate(`/hr/training-review/${r.id}`)} />
                  {r.status === 'under_review' && (
                    <>
                      <IconActionButton icon={Check} label="Approve" onClick={() => decideMutation.mutate({ id: r.id, decision: 'approved' })} />
                      <IconActionButton icon={X} label="Reject" danger onClick={() => decideMutation.mutate({ id: r.id, decision: 'rejected' })} />
                      <ReturnAction onReturn={(comment) => trainingApi.hrReturn(r.id, comment)} queryKeysToInvalidate={[['training-pending-hr']]} />
                    </>
                  )}
                  {r.status === 'approved' && (
                    <>
                      <input placeholder="Certificate notes" value={certificateNotes[r.id] ?? ''} onChange={(e) => setCertificateNotes((prev) => ({ ...prev, [r.id]: e.target.value }))} />
                      <IconActionButton icon={CheckCircle2} label="Mark Completed" onClick={() => completeMutation.mutate(r.id)} />
                    </>
                  )}
                </span>
              ),
            },
          ]}
        />
          </div>
        </div>
      )}
    </div>
  );
}
